export type Scales = Record<string, Record<string, string>>;

export type Ramp = string | { value: string; note?: string };

export type ColorData = {
  scales: Scales;
  roles: Record<string, string>;
  onDark: Record<string, string>;
  // Chart and map colors. A value is a hex color or the name of another entry.
  ramps: Record<string, Ramp>;
};

const isLiteral = (ref: string) => ref.startsWith("#");

function parseRef(scales: Scales, ref: string): [string, string] {
  const [family, step] = ref.split(".");
  if (!scales[family]?.[step]) {
    throw new Error(`Unknown color reference "${ref}"`);
  }
  return [family, step];
}

export function resolveRef(scales: Scales, ref: string): string {
  if (isLiteral(ref)) return ref;
  const [family, step] = parseRef(scales, ref);
  return scales[family][step];
}

const toKebab = (name: string) =>
  name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

function declaration(scales: Scales, name: string, ref: string) {
  const value = isLiteral(ref)
    ? ref
    : `var(--color-${parseRef(scales, ref).join("-")})`;
  return `  --color-${toKebab(name)}: ${value};`;
}

function rampLine(ramps: Record<string, Ramp>, name: string, ramp: Ramp) {
  const { value, note } = typeof ramp === "string" ? { value: ramp } : ramp;
  if (!isLiteral(value) && !(value in ramps)) {
    throw new Error(`Unknown color ramp reference "${value}" in "${name}"`);
  }
  const css = isLiteral(value) ? value : `var(--color-${value})`;
  return `  --color-${name}: ${css};${note ? ` /* ${note} */` : ""}`;
}

export function buildColorCss({
  scales,
  roles,
  onDark,
  ramps,
}: ColorData): string {
  const scaleLines = Object.entries(scales).flatMap(([family, steps]) =>
    Object.entries(steps).map(
      ([step, hex]) => `  --color-${family}-${step}: ${hex};`,
    ),
  );
  const roleLines = Object.entries(roles).map(([name, ref]) =>
    declaration(scales, name, ref),
  );
  const onDarkLines = Object.entries(onDark).map(([name, ref]) =>
    declaration(scales, name, ref),
  );

  return [
    "/* Generated from src/theme/colors.ts. Do not edit. */",
    ":root {",
    ...scaleLines,
    "",
    ...roleLines,
    "",
    ...Object.entries(ramps).map(([name, ramp]) => rampLine(ramps, name, ramp)),
    "}",
    "",
    "/* Roles on the dark page background. Add the class to the element that holds them. */",
    ".on-dark {",
    ...onDarkLines,
    "}",
    "",
  ].join("\n");
}
